import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  styled,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

// Gradient Button
const GradientButton = styled(Button)(({ theme }) => ({
  fontFamily: '"Lexend", sans-serif',
  background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
  color: "#fff",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: 8,
  padding: "14px",
  width: "100%",
  marginTop: theme.spacing(2),
  "&:hover": {
    background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
    boxShadow: "0 4px 14px rgba(99, 102, 241, 0.3)",
  },
}));

export default function ResetPasswordPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleReset = async () => {
    setError("");
    setSuccess("");

    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      await axios.post("https://api.taasnet.com:4000/api/v1/auth/reset-password", {
        email,
        new_password: password,
      });

      setSuccess("Password updated successfully!");
      setTimeout(() => {
        navigate("/sign-in");
      }, 1500);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error || "Failed to reset password. Try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#f8f8f8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: isMobile ? "90%" : 500,
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          textAlign: "center",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            mb: 2,
            fontFamily: '"Lexend", sans-serif',
            fontWeight: 700,
          }}
        >
          Reset Password
        </Typography>

        <Typography
          variant="body2"
          sx={{ mb: 3, fontFamily: '"Lexend", sans-serif', color: "#666" }}
        >
          You’re resetting password for: <strong>{email}</strong>
        </Typography>

        <TextField
          label="New Password"
          type="password"
          fullWidth
          sx={{ mb: 2 }}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <TextField
          label="Confirm Password"
          type="password"
          fullWidth
          sx={{ mb: 2 }}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />

        {error && (
          <Typography sx={{ color: "red", fontSize: 14, mb: 1 }}>
            {error}
          </Typography>
        )}
        {success && (
          <Typography sx={{ color: "green", fontSize: 14, mb: 1 }}>
            {success}
          </Typography>
        )}

        <GradientButton onClick={handleReset} disabled={loading}>
          {loading ? (
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          ) : (
            "Reset Password"
          )}
        </GradientButton>
      </Paper>
    </Box>
  );
}
